import PropTypes from 'prop-types';
import addIndex from "ramda/es/addIndex";
import compose from "ramda/es/compose";
import filter from "ramda/es/filter";
import map from "ramda/es/map";
import when from "ramda/es/when";
import cx from "ramda-extension/es/cx";
import React, { Children, cloneElement } from 'react';
const TableResponsiveRow = ({ className, children, columnDescriptor, changeRowspanOnClick, currentRowspanForRow, onResponsiveClick, rowOrder, disabled })=>{
    const responsiveDecorator = (isResponsive)=>function(child, index) {
            const columnSpecifications = columnDescriptor?.colSpec;
            let realColumnIndex = index;
            // support for rowSpan columns from left to right side
            if (columnSpecifications) {
                const headerAndRealColumnCountDifference = columnSpecifications.length - children.length;
                if (headerAndRealColumnCountDifference > 0) {
                    realColumnIndex += headerAndRealColumnCountDifference;
                }
            }
            const foundDescriptor = columnSpecifications?.[realColumnIndex];
            return /*#__PURE__*/ cloneElement(child, {
                responsiveMode: isResponsive,
                descriptor: foundDescriptor
            });
        };
    const withResponsiveClick = (child)=>/*#__PURE__*/ cloneElement(child, {
            onResponsiveClick: ()=>{
                when(()=>onResponsiveClick, onResponsiveClick)(rowOrder);
                when(()=>changeRowspanOnClick, changeRowspanOnClick)(rowOrder);
            }
        });
    const rowspanDecorator = (child)=>{
        if (child.props.rowSpan && currentRowspanForRow && !isNaN(currentRowspanForRow[this.props.rowOrder])) {
            return /*#__PURE__*/ cloneElement(child, {
                rowSpan: currentRowspanForRow[this.props.rowOrder]
            });
        }
        return child;
    };
    // columns hidden in extra row
    let responsiveChildren = Children.map(children, responsiveDecorator(true));
    // on responsive line, render only those children that are not visible, or have no descriptor
    responsiveChildren = Children.toArray(responsiveChildren).filter((child)=>child.props.descriptor ? child.props.descriptor.visible === false : true);
    // add to appropriate column the "expand" button, when necessary
    const arrayOfChildren = Children.toArray(children);
    const decorateChildren = compose(rowspanDecorator, withResponsiveClick, responsiveDecorator(false));
    const mapIndexed = addIndex(map);
    const visibleColumns = compose(filter((val)=>val.props.descriptor?.visible), mapIndexed(decorateChildren))(arrayOfChildren);
    // add expand button (responsiveToggle) to first visible column
    const numberOfInvisibleCols = responsiveChildren.filter((child)=>!child.props.visible).length;
    const columnIndexToAddToggle = arrayOfChildren.findIndex((child)=>!child.props.visible && !child.props.rowSpan);
    if (visibleColumns.length > 0 && numberOfInvisibleCols > 0 && columnIndexToAddToggle !== -1) {
        visibleColumns[columnIndexToAddToggle] = /*#__PURE__*/ cloneElement(visibleColumns[columnIndexToAddToggle], {
            responsiveToggle: 'prepend'
        });
    }
    return /*#__PURE__*/ React.createElement("tr", {
        className: cx('Table-row', className, {
            'Table-row--disabled': disabled
        })
    }, visibleColumns);
};
TableResponsiveRow.propTypes = {
    /**
	 * After click on responsive toggle to show/hide hidden columns it will increase rowspan of cell in row which row-spanned this row.
	 *
	 * @param {index} Index of current row which acts with toggle.
	 * @return {undefined}
	 */ changeRowspanOnClick: PropTypes.func,
    /**
	 * Children will be rendered within the `TableResponsiveRow`.
	 */ children: PropTypes.node,
    /**
	 * Custom `className` for the root.
	 */ className: PropTypes.string,
    /**
	 * Descriptor of all columns (generated from header) which is added to the component on TableBody level.
	 */ columnDescriptor: PropTypes.object,
    /**
	 * This value is fulfilled on TableBody level as value to override original rowSpan value of row due to responsive HIDE mode.
	 */ currentRowspanForRow: PropTypes.object,
    /**
	 * If true, the row will be disabled.
	 */ disabled: PropTypes.bool,
    /**
	 * Show/hide additional row in the table with hidden columns.
	 *
	 * @param {index} Identifies order number of row.
	 * @return {undefined}
	 */ onResponsiveClick: PropTypes.func,
    /**
	 * Represents row's order number.
	 */ rowOrder: PropTypes.number
};
TableResponsiveRow.displayName = 'TableResponsiveRow';
export default TableResponsiveRow;
